import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import PostForm from '../components/posts/PostForm';
import PostCard from '../components/posts/PostCard';
import Button from '../components/common/Button';
import useAuth from '../hooks/useAuth';
import usePost from '../hooks/usePost';
import { Globe, Shield, Heart } from "lucide-react"

/**
 * About page
 * @returns {JSX.Element} About page component
 */
const About = () => {

    return (
        <>
            <div className="bg-gray-50 dark:bg-gray-900">
                {/* Hero Section */}
                <section className="py-20 sm:py-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h1 className="text-4xl font-bold sm:text-5xl lg:text-6xl bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                Our Mission
                            </h1>
                            <p className="mt-6 text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                                Empowering people to build meaningful connections and thriving communities in the digital age.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Values Section */}
                <section className="py-20 bg-white dark:bg-gray-800">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold">Our Values</h2>
                            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">The principles that guide everything we do</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                {
                                    icon: Heart,
                                    title: "Community First",
                                    description: "We believe in the power of community to create positive change.",
                                },
                                {
                                    icon: Shield,
                                    title: "Trust & Safety",
                                    description: "Creating safe spaces for authentic connections and discussions.",
                                },
                                {
                                    icon: Globe,
                                    title: "Inclusivity",
                                    description: "Building platforms that welcome and celebrate diversity.",
                                },
                            ].map((value, index) => (
                                <div
                                    key={index}
                                    className="relative group bg-gray-50 dark:bg-gray-700 p-8 rounded-xl hover:shadow-lg transition-shadow"
                                >
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <div className="relative bg-gray-50 dark:bg-gray-700 p-8 rounded-xl">
                                        <value.icon className="w-12 h-12 text-indigo-600 dark:text-indigo-400 mb-4" />
                                        <h3 className="text-xl font-bold mb-2">{value.title}</h3>
                                        <p className="text-gray-600 dark:text-gray-300">{value.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Story Section */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-bold mb-6">Our Story</h2>
                                <div className="space-y-4 text-gray-600 dark:text-gray-300">
                                    <p>
                                        Founded in 2024, Vernetzung emerged from a simple observation: while the world was more connected than
                                        ever, meaningful communities were becoming harder to build and maintain.
                                    </p>
                                    <p>
                                        We set out to create a platform that would make it easier for people to find their tribe, share their
                                        passions, and build lasting connections. Our name, Vernetzung, means "networking" in German,
                                        reflecting our commitment to meaningful interconnections.
                                    </p>
                                    <p>
                                        Today, we're proud to support thousands of communities across the globe, each with its own unique
                                        purpose and culture. But we're just getting started.
                                    </p>
                                </div>
                            </div>
                            <div className="relative">
                                <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden">
                                    <img src="/placeholder.svg?height=400&width=600" alt="Team collaboration" className="object-cover" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stats Section */}
                <section className="py-20 bg-white dark:bg-gray-800">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                { number: "10K+", label: "Communities" },
                                { number: "1M+", label: "Active Users" },
                                { number: "150+", label: "Countries" },
                            ].map((stat, index) => (
                                <div key={index} className="text-center">
                                    <div className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                        {stat.number}
                                    </div>
                                    <div className="mt-2 text-gray-600 dark:text-gray-300">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Team Section */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl font-bold">Our Team</h2>
                            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300">Meet the people building Vernetzung</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                {
                                    name: "Sarah Johnson",
                                    role: "CEO & Co-founder",
                                    image: "/placeholder.svg?height=300&width=300",
                                },
                                {
                                    name: "Michael Chen",
                                    role: "CTO & Co-founder",
                                    image: "/placeholder.svg?height=300&width=300",
                                },
                                {
                                    name: "Emma Williams",
                                    role: "Head of Product",
                                    image: "/placeholder.svg?height=300&width=300",
                                },
                                {
                                    name: "James Wilson",
                                    role: "Head of Community",
                                    image: "/placeholder.svg?height=300&width=300",
                                },
                            ].map((member, index) => (
                                <div key={index} className="text-center">
                                    <div className="relative group">
                                        <div className="aspect-w-1 aspect-h-1 rounded-xl overflow-hidden mb-4">
                                            <img src={member.image || "/placeholder.svg"} alt={member.name} className="object-cover" />
                                        </div>
                                        <h3 className="text-lg font-semibold">{member.name}</h3>
                                        <p className="text-gray-600 dark:text-gray-300">{member.role}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default About;